/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {Entity, Link} from '@ion/core/src/types/entities';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import useExtendStyles from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import IdentifiersList from './IdentifiersList';

interface IdentifierDialogType {
  setShowDialog?: (showDialog: boolean) => void;
  onTitleClick?: (group) => void;
  showDialog?: boolean;
  data?: Link[];
  entity?: Entity;
  deviceName?: string;
};

const styleOverrides = (theme: DLSTheme) => ({
  formDialogContent : {
    overflow                       : 'auto',
    [theme.breakpoints.down('sm')] : {
      paddingTop : '8px !important'
    }
  },
  boxStyle : {
    marginTop : '14px',
    fontSize  : 'xx-large'
  }
});

/**
 * Defines the IdentifierDialog component. This component is used to
 * display all the groups in a dialog.
 *
 * ```typescript
 *
 *   import IdentifierDialog from './IdentifierDialog';
 *
 *   <IdentifierDialog />
 *
 * ```
 */
const IdentifierDialog = ({
  setShowDialog,
  onTitleClick,
  showDialog,
  deviceName,
  entity,
  data
}: IdentifierDialogType) => {
  const dialogStyles = useExtendStyles(styles, styleOverrides);
  const classes      = dialogStyles();

  const handleClose = function() {
    setShowDialog(false);
  };

  return (
    <Dialog open={showDialog}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={handleClose} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('admin-devices:grid.groupTitle', {counts: data?.length, deviceName})}
          </Typography>
        </div>
      </DialogTitle>
      <DialogContent className={classes.formDialogContent}>
        <Render.If condition={data?.length > 1}>
          <IdentifiersList data={data}
            onTitleClick={onTitleClick}
            boxStyle={classes.boxStyle}
            entity={entity} />
        </Render.If>
      </DialogContent>
      <DialogActions className={classes.formActions}>
        <TertiaryButton onClick={handleClose}>
          {translate('common:editors.shared.close')}
        </TertiaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(IdentifierDialog);
